function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { FavoriteButton } from './FavoriteButton';
import { Text, TextVariant } from '@naf/text';
import { Label } from '@naf/label';
import { spacing, borderRadius, themeLight, fontStyle, breakpoints } from '@naf/theme';
import { CardVariant } from './BaseCard';
import { propValidationConfig } from '../propValidationConfig';
const LinkCardWrapper = /*#__PURE__*/forwardRef(({
  id,
  href,
  children,
  onClick,
  ...linkProps
}, ref) => {
  if (href) {
    return /*#__PURE__*/React.createElement(LinkCardWrapperElement, _extends({
      id: id,
      tabIndex: 0,
      href: href,
      onClick: onClick,
      ref: ref
    }, linkProps), children);
  }
  if (!href && onClick) {
    return /*#__PURE__*/React.createElement(ButtonCardWrapperElement, {
      id: id,
      tabIndex: 0,
      ref: ref,
      onClick: onClick
    }, children);
  }
  return /*#__PURE__*/React.createElement(DivCardWrapper, {
    id: id,
    ref: ref
  }, children);
});
export const CardBenefits = /*#__PURE__*/forwardRef(({
  isNotClickable = false,
  isInternalLink = false,
  isFavorite = undefined,
  onFavoriteClick,
  advancedImage,
  img,
  title,
  headerTag = 'h3',
  topLabel,
  bottomLabel,
  logo,
  href,
  variant = CardVariant.Standard,
  linkProps,
  onClick,
  linkRef,
  ...rest
}, ref) => {
  const innerContent = /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Container, null, topLabel && /*#__PURE__*/React.createElement(TopLabel, topLabel), (img || advancedImage) && /*#__PURE__*/React.createElement(ImgWrap, null, advancedImage && advancedImage, img && !advancedImage && /*#__PURE__*/React.createElement("img", {
    src: img.src,
    alt: img.alt,
    draggable: "false"
  })), bottomLabel && /*#__PURE__*/React.createElement(BottomLabel, bottomLabel)), logo && /*#__PURE__*/React.createElement(PartnerIcon, null, logo), /*#__PURE__*/React.createElement(StyledText, {
    tag: headerTag,
    variant: TextVariant.Header3
  }, /*#__PURE__*/React.createElement(HoverWrap, null, title)));
  return /*#__PURE__*/React.createElement(BenefitCard, _extends({
    isNotClickable: isNotClickable,
    variant: variant,
    ref: ref
  }, rest), isFavorite !== undefined && /*#__PURE__*/React.createElement(BenefitFavorite, {
    isFavorite: isFavorite,
    onClick: () => {
      if (onFavoriteClick) {
        onFavoriteClick(isFavorite);
      }
      return true;
    }
  }), isNotClickable || isInternalLink ? /*#__PURE__*/React.createElement(DivCardWrapper, null, innerContent) : /*#__PURE__*/React.createElement(LinkCardWrapper, _extends({
    href: href,
    onClick: onClick,
    ref: linkRef
  }, linkProps), innerContent));
});
const cardWrapperStyle = css(["flex-basis:100%;width:fit-content;display:flex;text-decoration:none;"]);
const resetButtonStyles = css(["border:none;margin:0;padding:0;width:auto;overflow:visible;background:transparent;color:inherit;font:inherit;line-height:normal;touch-action:manipulation;appearance:none !important;"]);
const LinkCardWrapperElement = styled.a.withConfig({
  displayName: "CardBenefits__LinkCardWrapperElement",
  componentId: "sc-u4zdey-0"
})(["", ";border:none;flex-direction:column;&:focus,&:focus-visible{margin:2px;}"], cardWrapperStyle);
const ButtonCardWrapperElement = styled.button.withConfig({
  displayName: "CardBenefits__ButtonCardWrapperElement",
  componentId: "sc-u4zdey-1"
})(["", " ", " cursor:pointer;text-align:start;&:focus,&:focus-visible{margin:2px;}"], resetButtonStyles, cardWrapperStyle);
const DivCardWrapper = styled.div.withConfig({
  displayName: "CardBenefits__DivCardWrapper",
  componentId: "sc-u4zdey-2"
})(["", ";border:none;flex-direction:column;"], cardWrapperStyle);
const Container = styled.div.withConfig({
  displayName: "CardBenefits__Container",
  componentId: "sc-u4zdey-3"
})(["position:relative;margin-top:", ";"], spacing.space8);
const HoverWrap = styled.span.withConfig({
  displayName: "CardBenefits__HoverWrap",
  componentId: "sc-u4zdey-4"
})(["background-size:0 2px;background-position:0 100%;background-image:linear-gradient( 90deg,", ",", " );background-repeat:no-repeat;transition:background-size 0.3s ease;text-decoration:none;display:inline;"], ({
  theme
}) => theme.componentColors ? theme.componentColors.interactiveElement.active : themeLight.componentColors.interactiveElement.active, ({
  theme
}) => theme.componentColors ? theme.componentColors.interactiveElement.active : themeLight.componentColors.interactiveElement.active);
const BenefitCard = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "CardBenefits__BenefitCard",
  componentId: "sc-u4zdey-5"
})(["overflow:hidden;position:relative;display:inline-flex;width:", ";", ";margin-bottom:", ";", " a,a:visited,a:hover,a:active{color:", ";}", " ", ""], ({
  width
}) => !width ? 'fit-content' : typeof width === 'number' ? `${width}px` : width, fontStyle.bodyText.bodyText, spacing.space48, ({
  isNotClickable
}) => isNotClickable ? null : css(["cursor:pointer;&:hover{img{transform:scale(1.02);}", "{background-size:100% 2px;}}"], HoverWrap), ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, ({
  variant
}) => variant === CardVariant.Small && css(["min-width:calc(25% - (", " * 0.75));max-width:calc(25% - (", " * 0.75));box-sizing:border-box;margin-right:", ";margin-bottom:", ";&:last-child{margin-right:0;}figure{width:100%;height:0;padding-bottom:56.25%;}@media (max-width:", "){min-width:75%;margin-right:", ";}"], spacing.space32, spacing.space32, spacing.space32, spacing.space16, breakpoints.m, spacing.space24), ({
  variant
}) => variant === CardVariant.Standard && css(["min-width:calc(33% - (", " * (2 / 3)));figure{width:100%;height:0;padding-bottom:56.25%;}@media (max-width:", "){display:flex;width:100%;}"], spacing.space32, breakpoints.m));
const ImgWrap = styled.figure.withConfig({
  displayName: "CardBenefits__ImgWrap",
  componentId: "sc-u4zdey-6"
})(["position:relative;margin:0;flex:none;border-radius:", ";overflow:hidden;img{border-radius:", ";position:absolute;display:block;height:100%;width:100%;object-fit:cover;transition:transform 0.6s ease;pointer-events:none;}"], borderRadius.S, borderRadius.S);
const BenefitFavorite = styled(FavoriteButton).withConfig({
  displayName: "CardBenefits__BenefitFavorite",
  componentId: "sc-u4zdey-7"
})(["position:absolute;z-index:1;top:", ";right:", ";"], spacing.space8, spacing.space8);
const PartnerIcon = styled.div.withConfig({
  displayName: "CardBenefits__PartnerIcon",
  componentId: "sc-u4zdey-8"
})(["width:fit-content;height:", ";margin-top:", ";&:hover{img{transform:none;}}"], spacing.space24, spacing.space16);
const StyledText = styled(Text).withConfig({
  displayName: "CardBenefits__StyledText",
  componentId: "sc-u4zdey-9"
})(["margin:", " 0 0 0;"], spacing.space8);
const TopLabel = styled(Label).withConfig({
  displayName: "CardBenefits__TopLabel",
  componentId: "sc-u4zdey-10"
})(["position:absolute;z-index:1;left:", ";top:-", ";"], spacing.space8, spacing.space8);
const BottomLabel = styled(Label).withConfig({
  displayName: "CardBenefits__BottomLabel",
  componentId: "sc-u4zdey-11"
})(["position:absolute;z-index:1;right:", ";bottom:-6px;"], spacing.space8);
//# sourceMappingURL=CardBenefits.js.map